// src/views/admin/RoadWorksPage.js
// FlowSense MOW+ — Road Infrastructure Command Center
// v4.5 | 🚀 SESSION SECURITY: Auto-logout on 401/AuthError
//        - fetchData: catchNonAuth helper lets 401s bubble up while silencing other errors
//        - fetchData: Auto-logout with toast on token expiry detected during data fetch
//        - Event Bus: request:error listener catches background AuthErrors (health probes)
//          and triggers the same graceful logout flow
// Features: Live Road Network, Condition Heatmap, Citizen Issue Stream,
//           Inspection Scheduler, Trend Analytics, Bulk Status Engine,
//           Glass Morphism UI, Framer Motion Animations, Real-time Pulse System,
//           AI Confidence Scoring, Predictive PCI, Health Telemetry,
//           Live WebSocket Pub/Sub Integration, GIS Map Tab,
//           Explicit WebSocket Lifecycle Logging (connect/disconnect),
//           🚀 v4.2 UPDATE: Interactive Road Drawing Mode + Unmapped Roads Panel
//           🚀 v4.3 UPDATE: AI Insights Panel — Live WebSockets + ML Engine Connected
//           🚀 v4.4 UPDATE: RoadAIEngineService fully wired — Event Bus, Health Monitor,
//                           Batch Prediction Trigger, Circuit Breaker UI Feedback
//           🚀 v4.5 UPDATE: Session Security — catchNonAuth + background AuthError logout
//           🚀 v4.6 UPDATE: AI Road State Sync Modal + RUL Scores in Inventory Table
//           🚀 v4.7 UPDATE: RoadTableRow extracted, RoadSystemWatchdogPanel integrated,
//                           Server-side sort + ML-Starved filter, Global unmapped count
// --- UPDATE: v5.0 — Componentised + performance pass + AI/ML visual system
//   Structure (was one 3,100-line file):
//     hooks/useRoadWorksData.js                 data, filters, polling, WebSocket, AI event bus
//     components/roads/page/shared.js           motion presets, apiFetch, DebugLogger, cards
//     components/roads/page/AiMlIcons.js        custom AI/ML icon set + AiChip/AiGlyph/ModelTag
//     components/roads/page/RoadPageHeader.js   header + actions
//     components/roads/page/AiCommandStrip.js   AI/ML KPI strip (RUL, risk, anomalies, engine)
//     components/roads/page/AnalyticsPanels.js  condition / surface / inspections / health
//     components/roads/page/AIInsightsPanel.js  insights feed with tabs, dismiss, confidence
//     components/roads/page/RoadInventoryTable.js  redesigned inventory table
//     components/roads/page/RoadActionModals.js assessment / inspection / issue / AI sync
//     components/roads/page/QuickReferencePanel.js, RoadDebugDrawer.js
//   Performance fixes (see useRoadWorksData.js header): debounced search, stale-response
//   guard, parallel anomaly fetch, single fetch per sort/filter, leak-free one-time WebSocket
//   subscriptions, coalesced live refresh, visibility-aware polling, single auth-expiry path.
//   Debug log stream now lives in the drawer, so logging no longer re-renders the page.
// --- END UPDATE
// --- UPDATE: v5.1 — Road system ↔ AI/ML connection
//   • Inventory rows: AI prediction cell + expandable explainable analysis (RoadAiPrediction)
//     whose recommended actions open the existing WO / inspection / assessment / map flows.
//   • "Refresh AI prediction" in a row and "Run RUL prediction" in the AI Command Center
//     both run the batch RSF prediction (handleForecastRisk) and refresh the data.
//   • AI Command Center gets the loaded roads → band distribution, ML coverage, top-risk
//     roads (click opens the road), readiness and blocked models; circuit state in header.
//   • Per-road insights + live prediction patches come from useRoadWorksData v5.1.
// --- END UPDATE
// --- UPDATE: v5.2 — Road Works Report tab
//   • Third tab "Road Works Report" renders <RoadWorksReportPanel /> (FlowSense AI, sliced
//     segments + ML evaluation, sub-assets, asphalt logistics & fleet, linked work orders with
//     QA audit, tick-box inspections) for the chosen road.
//   • Road picker in the tab (filterable, current inventory page + the road being reported).
//   • Opened from: inventory row menu "Works report", clicking a road on the GIS map,
//     the View modal AI intent 'REPORT', or a deep link ?tab=report&road=<id>.
//   • Inspections / QA audits / ML evaluations done in the report refresh the page KPIs.
// --- END UPDATE
// --- UPDATE: v5.3 — Road Works Report table (replaces the v5.2 road picker)
//   • <RoadReportSelectorTable /> above the report: every road on the inventory page with
//     segment count / length / potholes, ML segment PCI + treatment, AI remaining life,
//     AI priority with next action, work orders, QA and inspections (GET /roads/report-summary).
//   • Click a row → the report below switches to that road and scrolls into view.
//   • Table paging drives the shared inventory page; map / AI / details actions reuse the
//     page handlers; report changes (inspection, QA, ML run) refresh the table roll-up too.
// --- END UPDATE
// --- UPDATE: v5.4 — Drive Mode AI review + road-damage detection model
//   • Dashboard: network Drive Mode AI detections (photos with AI boxes, damage by type, roads with
//     the most detections → opens the Works Report) and the detection model panel (accuracy,
//     training data, Train). Review answers train the model and update segment health.
//   • Works Report → "Drive AI detections" tab per road (damage per segment, review).
//   • AI Command Center (map) shows the detection model with a Train button.
// --- END UPDATE

// --- UPDATE: v5.5 — saved road edits show straight away
//   • After Save the open road (View / Edit modals, the row's AI helper) kept the old object, so a
//     saved install date or criticality looked unsaved until the page was reloaded. The saved
//     values are merged into the active road immediately, and the active road is refreshed from the
//     reloaded list.
// --- END UPDATE
// --- UPDATE: v5.6 — GIS map segment health layer
//   • Map toolbar (RoadMapLayerControls): Roads | Segment health, Markers / AI detections / PCI
//     labels switches, health band chips with counts, network numbers, "Slice N roads".
//   • Segment health mode draws every segment of the mapped roads coloured by PCI (GET
//     /roads/segments/map); click a segment for its details and "Open Works Report".
//   • Settings are remembered on this device; segments refresh live when segment health changes.
// --- END UPDATE
// --- UPDATE: v5.7 — GIS map shows the whole network
//   • "Show: Whole network" (default) draws every mapped road in the system — in Segment health
//     mode every segment, in Roads mode every road line — not only the 50 roads on the inventory
//     page. "Inventory page" keeps the old behaviour (follows the table's page and filters).
//   • Network roads that are not on the inventory page are added to the map as road lines (no
//     pins); clicking one selects it for the Works Report like any other road.
//   • "Fit network" zooms to every road shown. Slicing many roads asks for confirmation first.
// --- END UPDATE
// --- UPDATE: v5.8 (roads v1.3) — road ponding forecast (live rain × drainage × TessaDEM)
//   • Map tab: "Where rain will settle on the roads" card — signal, hourly rain vs segments with water, hour
//     scrubber / playback, the worst segments (depth, when it starts, how long it stays, why) → fly to / Works Report.
//   • Map layer: ponding segments + settling points sized by depth, dashed line to the linked drain (red when that
//     drain is forecast to fail); follows the selected hour.
//   • Live: recomputed after every drainage outlook run (websocket `road_ponding_forecast`); the page warns the user
//     when the forecast gets worse, on any tab.
// --- END UPDATE
// --- UPDATE: v5.9 (roads v1.4)
//   • The ponding layer + legend render inside GisMapView (correct map lifecycle on tab switches).
//   • Map toolbar: "Rain water" switch + forecast chip; AI Command Center: rain-water section.
//   • Roads carry ai_ponding → AI risk index, prediction card, inventory and report tables, CSV.
// --- END UPDATE
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Box, Tabs, TabList, TabPanels, Tab, TabPanel, useColorModeValue, Badge, HStack, Icon,
    Text, VStack, Flex, useToast, SimpleGrid, Grid, Spinner, useBreakpointValue, useDisclosure,
    // --- UPDATE: works report tab
    // --- END UPDATE
} from '@chakra-ui/react';
import {
    MdAssessment, MdBuild, MdSchedule, MdDashboard, MdOutlineMap, MdEditLocation,
    MdConstruction, // --- UPDATE: works report tab
    MdPhoneInTalk, // --- UPDATE (Phone AI Helpdesk): public reports tab
} from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
import { AnimatePresence } from 'framer-motion';

import * as roadService from '../../services/roadService';
import roadAIEngineService from '../../services/roadAIEngineService';
import { useNotifications } from '../../contexts/NotificationContext';
import CreateRoadModal from '../../components/roads/CreateRoadModal';
import ViewRoadModal from '../../components/roads/ViewRoadModal';
import EditRoadModal from '../../components/roads/EditRoadModal';
import GisMapView from '../../components/public_utilities/GisMapView';
import RoadMapDrawingPanel from '../../components/roads/RoadMapDrawingPanel';
import QuickWorkOrderModal from '../../components/roads/QuickWorkOrderModal';
import { RoadSystemWatchdogPanel } from '../../components/roads/RoadSystemWatchdog';
import AIOperationsPanel from '../../components/roads/AIOperationsPanel';
import AIClusteredWorkOrdersTable from '../../components/roads/AIClusteredWorkOrdersTable';

// --- UPDATE: v5.0 extracted components + data hook
import useRoadWorksData from '../../hooks/useRoadWorksData';
import { ANIM, DebugLogger, MotionBox, StatCard, RUL_BANDS, rulBand } from '../../components/roads/page/shared';
import { AiChip } from '../../components/roads/page/AiMlIcons';
import RoadPageHeader from '../../components/roads/page/RoadPageHeader';
import AiCommandStrip from '../../components/roads/page/AiCommandStrip';
import {
    ConditionDistributionPanel, SurfaceTypePanel, UpcomingInspectionsPanel, SystemHealthPanel,
} from '../../components/roads/page/AnalyticsPanels';
import AIInsightsPanel from '../../components/roads/page/AIInsightsPanel';
import RoadInventoryTable from '../../components/roads/page/RoadInventoryTable';
import {
    ConditionAssessmentModal, ScheduleInspectionModal, ReportIssueModal, AIRoadStateSyncModal,
} from '../../components/roads/page/RoadActionModals';
import QuickReferencePanel from '../../components/roads/page/QuickReferencePanel';
import RoadDebugDrawer from '../../components/roads/page/RoadDebugDrawer';
// --- UPDATE: v5.2 works report tab
import RoadWorksReportPanel from '../../components/roads/RoadWorksReportPanel';
import RoadReportSelectorTable from '../../components/roads/RoadReportSelectorTable'; // v5.3
// --- UPDATE (Phone AI Helpdesk): phone / public road reports for managers & supervisors
import RoadPublicReportsPanel from '../../components/roads/RoadPublicReportsPanel';
// --- END UPDATE
// --- END UPDATE
// --- UPDATE: v5.4 Drive Mode AI review
import RoadDriveDetectionsPanel from '../../components/roads/drive/RoadDriveDetectionsPanel';
import RoadVisionModelPanel from '../../components/roads/drive/RoadVisionModelPanel';
// --- END UPDATE
// --- UPDATE: v5.6 segment health layer
import RoadMapLayerControls from '../../components/roads/RoadMapLayerControls';
import useRoadSegmentMap from '../../hooks/useRoadSegmentMap';
import { BAND_KEYS, filterSegments, summarize } from '../../components/roads/roadSegmentHealth';
// --- END UPDATE
// --- UPDATE: v5.8 road ponding forecast
import useRoadPondingForecast from '../../components/roads/ponding/useRoadPondingForecast';
import RoadPondingPanel from '../../components/roads/ponding/RoadPondingPanel';
import { SIGNAL_META as PONDING_SIGNAL, fmtDepth as fmtPondDepth, fmtHours as fmtPondHours } from '../../components/roads/ponding/pondingUi';
// --- END UPDATE
// --- END UPDATE

// --- UPDATE: v5.2 tab indexes (order of <Tab>s below)
const TAB_DASHBOARD = 0;
const TAB_MAP = 1;
const TAB_REPORT = 2;
const TAB_PUBLIC = 3; // --- UPDATE (Phone AI Helpdesk)
const TAB_BY_NAME = { dashboard: TAB_DASHBOARD, map: TAB_MAP, report: TAB_REPORT, public: TAB_PUBLIC, reports: TAB_PUBLIC };
/** ?tab=report&road=<id> deep link (read once on mount; works with any router). */
const readDeepLink = () => {
    try {
        const q = new URLSearchParams(window.location.search);
        const tab = TAB_BY_NAME[String(q.get('tab') || '').toLowerCase()];
        const road = q.get('road') || q.get('road_id');
        return { tab: tab ?? (road ? TAB_REPORT : null), road: road || null, issue: q.get('issue') || null }; // --- UPDATE: issue
    } catch {
        return { tab: null, road: null, issue: null };
    }
};
// --- END UPDATE

// --- UPDATE: v5.6 map layer settings remembered per device (never required)
const MAP_PREFS_KEY = 'flowSense_roadMapLayers';
const readMapPrefs = () => {
    try { return JSON.parse(window.localStorage.getItem(MAP_PREFS_KEY) || 'null') || {}; } catch { return {}; }
};
// --- END UPDATE

// Stable empty props so GisMapView doesn't see new references every render
const EMPTY_ARR = [];
const EMPTY_OBJ = {};

// A road is only considered mapped if it has a drawn path (polyline_points)
const isRoadMapped = (r) => {
    let polyData = r.polyline_points;
    if (!polyData) return false;
    if (typeof polyData === 'string') {
        try { polyData = JSON.parse(polyData); } catch (e) { return false; }
    }
    return Array.isArray(polyData) && polyData.length > 1;
};

// Haversine length of a drawn polyline in metres
const polylineLengthM = (points) => {
    let lengthM = 0;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1], b = points[i];
        const R = 6371000;
        const dLat = (b.lat - a.lat) * Math.PI / 180;
        const dLng = (b.lng - a.lng) * Math.PI / 180;
        const lat1 = a.lat * Math.PI / 180;
        const lat2 = b.lat * Math.PI / 180;
        const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
        lengthM += 2 * R * Math.asin(Math.sqrt(x));
    }
    return lengthM;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const RoadWorksPage = () => {
    const logger = useRef(new DebugLogger('RoadWorksPage')).current;
    const toast = useToast();
    const isMobile = useBreakpointValue({ base:true, md:false });
    const { isConnected } = useNotifications();

    // --- UPDATE v5.8/v5.9: road ponding forecast — loaded on every tab (the user is warned when it gets worse) and
    // merged onto the roads (ai_ponding) by useRoadWorksData
    const handlePondingEscalate = useCallback((signal, d) => {
        const top = d?.segments?.[0];
        toast({
            title: `Road ponding forecast: ${PONDING_SIGNAL[signal]?.label || signal}`,
            description: `${d?.summary?.ponding_segments ?? 0} segment(s) on ${d?.summary?.roads_affected ?? 0} road(s) will hold water${top ? ` — worst ${top.road_name} (${fmtPondDepth(top.peak_depth_m)}, from ${fmtPondHours(top.time_to_ponding_hours)})` : ''}. Open the Map tab to see where.`,
            status: signal === 'warning' ? 'error' : 'warning', duration: 9000, isClosable: true, position: 'top-right',
        });
    }, [toast]);
    const ponding = useRoadPondingForecast({ enabled: true, onEscalate: handlePondingEscalate });
    // --- END UPDATE

    // --- UPDATE: data/filter/live state lives in the hook
    const data = useRoadWorksData({ logger, toast, pondingRoads: ponding.data?.roads }); // --- UPDATE v5.9: + pondingRoads
    const {
        analytics, liveAiEvents, batchInsights, roads, lookups, isLoading, totalRoads,
        totalUnmappedRoads, unclusteredCount, rawAnomalies, lastRefresh, aiStatus, aiCircuit,
        fetchError, fetchData, page, limit, conditionFilter,
        setConditionFilter, setStatusFilter, setShowOnlyStarved, setPage,
        autoRefresh, setAutoRefresh,
    } = data;
    // --- END UPDATE

    // ── UI state ─────────────────────────────────────────────────────────────
    const [isExporting, setIsExporting] = useState(false);
    const [woInitialValues, setWoInitialValues] = useState({});
    const [isClustering, setIsClustering] = useState(false);
    const [heatmapActive, setHeatmapActive] = useState(false);
    const [showQuickRef, setShowQuickRef] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [bulkStatus, setBulkStatus] = useState('');
    const [isBulkRunning, setIsBulkRunning] = useState(false);
    // --- UPDATE: deep link picks the starting tab / report road
    const deepLink = useMemo(readDeepLink, []);
    const [activeTab, setActiveTab] = useState(deepLink.tab ?? TAB_DASHBOARD);
    const [reportRoad, setReportRoad] = useState(deepLink.road ? { id: deepLink.road, name: null } : null);
    const [reportRefreshKey, setReportRefreshKey] = useState(0); // v5.3: bumps the table roll-up
    // --- UPDATE (Phone AI Helpdesk): open public road reports (tab badge)
    const [publicOpenCount, setPublicOpenCount] = useState(0);
    // --- END UPDATE
    const reportPanelRef = useRef(null);
    // --- END UPDATE
    const [roadBeingMapped, setRoadBeingMapped] = useState(null);
    const [isSavingGeometry, setIsSavingGeometry] = useState(false);
    const [activeRoad, setActiveRoad] = useState(null);
    // --- UPDATE
    const [rulFilter, setRulFilter] = useState(null);       // client-side AI risk band filter
    const [isForecasting, setIsForecasting] = useState(false);
    // --- END UPDATE
    // --- UPDATE: v5.6 map layers
    const mapPrefs = useMemo(readMapPrefs, []);
    const [mapMode, setMapMode] = useState(mapPrefs.mode === 'roads' ? 'roads' : 'segments');
    const [showMapMarkers, setShowMapMarkers] = useState(mapPrefs.markers !== false);
    const [showMapAnomalies, setShowMapAnomalies] = useState(mapPrefs.anomalies !== false);
    const [showSegmentLabels, setShowSegmentLabels] = useState(!!mapPrefs.labels);
    const [segmentBands, setSegmentBands] = useState(() => new Set(Array.isArray(mapPrefs.bands) && mapPrefs.bands.length ? mapPrefs.bands.filter(b => BAND_KEYS.includes(b)) : BAND_KEYS));
    const [selectedSegmentId, setSelectedSegmentId] = useState(null);
    const [mapScope, setMapScope] = useState(mapPrefs.scope === 'page' ? 'page' : 'network'); // --- UPDATE v5.7
    const mapInstanceRef = useRef(null); // --- UPDATE v5.7
    // --- UPDATE v5.8 (v5.9: the ponding layer now renders inside GisMapView)
    const [showPondingLayer, setShowPondingLayer] = useState(mapPrefs.ponding !== false);
    const [pondingSegmentId, setPondingSegmentId] = useState(null);
    // --- END UPDATE
    useEffect(() => {
        try {
            window.localStorage.setItem(MAP_PREFS_KEY, JSON.stringify({ mode: mapMode, scope: mapScope, markers: showMapMarkers, anomalies: showMapAnomalies, labels: showSegmentLabels, bands: [...segmentBands], ponding: showPondingLayer })); // --- UPDATE v5.8: + ponding
        } catch { /* private mode / quota — settings just aren't remembered */ }
    }, [mapMode, mapScope, showMapMarkers, showMapAnomalies, showSegmentLabels, segmentBands, showPondingLayer]);
    // --- END UPDATE

    // Modals
    const conditionModal = useDisclosure();
    const inspectionModal = useDisclosure();
    const issueModal = useDisclosure();
    const woModal = useDisclosure();
    const debugDrawer = useDisclosure();
    const createRoadModal = useDisclosure();
    const viewRoadModal = useDisclosure();
    const editRoadModal = useDisclosure();
    const aiSyncModal = useDisclosure();

    // ── Theme ────────────────────────────────────────────────────────────────
    const bgColor     = useColorModeValue('gray.50','gray.900');
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.700');
    const overlayBg   = useColorModeValue('white','gray.800');
    const overlayText = useColorModeValue('gray.800','white');

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        logger.info('Page mounted — v5.0 componentised command center');
        return () => { isMounted.current = false; };
    }, [logger]);

    // ── Insight switchboard ─────────────────────────────────────────────────
    // --- UPDATE (Road insights v2): handlers declared further down, read at click time
    const insightExtrasRef = useRef({});
    // --- END UPDATE
    const handleInsightAction = useCallback((action) => {
        const { intent, payload = {} } = action || {};
        logger.info(`Insight action triggered: ${intent}`, payload);

        switch (intent) {
            case 'OPEN_QUICK_WO': {
                const targetRoad = roads.find(r => String(r.id) === String(payload.assetId)) || null;
                setActiveRoad(targetRoad);
                setWoInitialValues({
                    title: payload.title || 'AI Recommended Action',
                    category: payload.category || 'Predictive Maintenance',
                    priority: payload.priority || 'high',
                    description: payload.description || 'Auto-generated work order from FlowSense AI Insight.'
                });
                woModal.onOpen();
                break;
            }
            case 'FILTER_ROADS':
                if (payload.condition) setConditionFilter(payload.condition);
                if (payload.status) setStatusFilter(payload.status);
                break;
            case 'HIGHLIGHT_MAP':
                setActiveTab(1);
                if (payload.heatmap) insightExtrasRef.current.setHeatmapActive?.(true); // --- UPDATE
                toast({ title: 'Switched to Map View', status: 'info', duration: 2000 });
                break;
            // --- UPDATE (Road insights v2)
            case 'RUN_RUL':
                insightExtrasRef.current.handleForecastRisk?.();
                break;
            case 'RUN_CLUSTERING':
                insightExtrasRef.current.handleRunClustering?.();
                break;
            case 'SHOW_STARVED':
                setShowOnlyStarved(true);
                setActiveTab(TAB_DASHBOARD);
                break;
            case 'OPEN_REPORT':
                if (payload.roadId) insightExtrasRef.current.handleOpenReport?.({ id: payload.roadId, name: payload.roadName || null });
                else setActiveTab(TAB_REPORT);
                break;
            case 'OPEN_TAB':
                setActiveTab(TAB_BY_NAME[payload.tab] ?? TAB_DASHBOARD);
                break;
            // --- END UPDATE
            case 'TRIGGER_ML_BATCH':
                roadAIEngineService.runBatchPredictiveAnalysis().then(() => fetchData(true)).catch(err => logger.error('ML batch failed', err));
                toast({ title: 'ML Batch Analysis Queued', status: 'info', duration: 3000 });
                break;
            case 'OPEN_BUDGET_MODAL':
                toast({ title: 'Budget Allocation', description: 'Budget module integration pending.', status: 'info', duration: 3000 });
                break;
            default:
                toast({ title: 'Action recognized', description: `Intent: ${intent}`, status: 'success' });
        }
    }, [roads, woModal, fetchData, toast, logger, setConditionFilter, setStatusFilter, setShowOnlyStarved]); // --- UPDATE

    // ── Header actions ──────────────────────────────────────────────────────
    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            await roadService.exportRoadsCsv();
            logger.success('CSV export complete');
            toast({ title: 'Export Successful', status: 'success', duration: 2000 });
        } catch (error) {
            logger.error('CSV export failed', error);
            toast({ title: 'Export Failed', status: 'error', duration: 3000 });
        } finally {
            if (isMounted.current) setIsExporting(false);
        }
    }, [toast, logger]);

    const handleForecastRisk = useCallback(async () => {
        if (isForecasting) return;
        setIsForecasting(true);
        try {
            logger.info('Batch ML prediction triggered from header');
            toast({ title: '🧠 ML Batch Triggered', description: 'Running Random Survival Forest predictions on road network…', status: 'info', duration: 4000, isClosable: true });
            await roadAIEngineService.runBatchPredictiveAnalysis();
            logger.success('Batch ML prediction complete');
            toast({ title: '✅ Analysis Complete', description: 'Predictive RUL scores updated.', status: 'success', duration: 3000 });
            fetchData(true);
        } catch (err) {
            logger.error('Batch ML prediction failed', err);
            toast({ title: 'ML Analysis Failed', description: err.message, status: 'error', duration: 4000 });
        } finally {
            if (isMounted.current) setIsForecasting(false);
        }
    }, [isForecasting, toast, fetchData, logger]);

    // ── Selection ───────────────────────────────────────────────────────────
    const toggleSelect = useCallback((id) => {
        setSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);

    // --- UPDATE: select/deselect the visible rows (works with the AI risk filter)
    const selectIds = useCallback((ids, mode) => {
        setSelected(prev => {
            const n = new Set(prev);
            ids.forEach(id => (mode === 'remove' ? n.delete(id) : n.add(id)));
            return n;
        });
    }, []);
    const clearSelection = useCallback(() => setSelected(new Set()), []);
    // --- END UPDATE

    const handleBulkStatusUpdate = useCallback(async () => {
        if (!selected.size || !bulkStatus) return;
        setIsBulkRunning(true);
        try {
            const res = await roadService.bulkUpdateRoadStatus(Array.from(selected), bulkStatus);
            logger.success('Bulk status updated', res?.data);
            toast({
                title: 'Bulk Update Complete',
                description: `${res?.data?.updated_count || 0} roads updated to ${bulkStatus}`,
                status:'success', duration:3000,
            });
            setSelected(new Set());
            setBulkStatus('');
            fetchData();
        } catch (err) {
            logger.error('Bulk update failed', err);
            toast({ title:'Bulk Update Failed', description:err.message, status:'error', duration:3000 });
        } finally {
            if (isMounted.current) setIsBulkRunning(false);
        }
    }, [selected, bulkStatus, toast, fetchData, logger]);

    // ── Row actions ─────────────────────────────────────────────────────────
    const handleView = useCallback((road) => {
        logger.info('View road details', { id: road.id });
        setActiveRoad(road);
        viewRoadModal.onOpen();
    }, [viewRoadModal, logger]);

    const handleEdit = useCallback((road) => { setActiveRoad(road); editRoadModal.onOpen(); }, [editRoadModal]);
    const handleAssess = useCallback((road) => { setActiveRoad(road); conditionModal.onOpen(); }, [conditionModal]);
    const handleScheduleInsp = useCallback((road) => { setActiveRoad(road); inspectionModal.onOpen(); }, [inspectionModal]);
    const handleReportIssue = useCallback((road) => { setActiveRoad(road); issueModal.onOpen(); }, [issueModal]);
    const handleCreateWO = useCallback((road) => { setActiveRoad(road); setWoInitialValues({}); woModal.onOpen(); }, [woModal]);

    // --- UPDATE: v5.2 open a road in the Road Works Report tab
    const handleOpenReport = useCallback((road) => {
        if (!road?.id) return;
        logger.info('Open road works report', { id: road.id });
        setReportRoad({ id: road.id, name: road.name || null });
        setActiveTab(TAB_REPORT);
    }, [logger]);
    // --- END UPDATE

    const submitEditRoad = useCallback(async (roadId, payload) => {
        try {
            await roadService.updateRoad(roadId, payload);
            // --- UPDATE: v5.5 the open road shows what was saved
            setActiveRoad(prev => (prev && String(prev.id) === String(roadId)
                ? { ...prev, ...payload, metadata: { ...(prev.metadata || {}), ...(payload.metadata || {}), ...(payload.install_date ? { last_paved_date: payload.install_date } : {}) } }
                : prev));
            // --- END UPDATE
            logger.success('Road updated', payload.name);
            toast({ title: 'Road Updated Successfully', status: 'success', duration: 3000 });
            fetchData(true);
        } catch (err) {
            logger.error('Failed to update road', err);
            toast({ title: 'Update Failed', description: err.message, status: 'error', duration: 4000 });
            throw err;
        }
    }, [toast, fetchData, logger]);

    const handleDelete = useCallback(async (road) => {
        if (!window.confirm(`Soft-delete road "${road.name}"? Open work orders will block deletion.`)) return;
        try {
            await roadService.deleteRoad(road.id);
            logger.success('Road deleted', { id: road.id });
            toast({ title:'Road deleted', status:'success', duration:2000 });
            setSelected(prev => { if (!prev.has(road.id)) return prev; const n = new Set(prev); n.delete(road.id); return n; });
            fetchData();
        } catch (err) {
            if (err.message?.includes('open work order')) {
                if (window.confirm(`${err.message}\n\nForce delete anyway?`)) {
                    try {
                        await roadService.deleteRoad(road.id, { force: true });
                        toast({ title:'Road force-deleted', status:'warning', duration:2000 });
                        fetchData();
                    } catch (err2) {
                        toast({ title:'Force delete failed', description:err2.message, status:'error', duration:3000 });
                    }
                }
            } else {
                toast({ title:'Delete failed', description:err.message, status:'error', duration:3000 });
            }
        }
    }, [toast, fetchData, logger]);

    // ── Map drawing ─────────────────────────────────────────────────────────
    const handleStartMappingRoad = useCallback((road) => {
        if (!road || !road.id) return;
        if (roadBeingMapped && roadBeingMapped.id !== road.id) {
            logger.warn('Switching draw target', { from: roadBeingMapped.id, to: road.id });
        }
        setRoadBeingMapped(road);
        setActiveTab(1);
        logger.info('Map drawing mode activated', { id: road.id, name: road.name });
        toast({
            title: '🎯 Drawing Mode Activated',
            description: `Click on the map to trace ${road.name}. Right-click to undo. Double-click the last point to save.`,
            status: 'info', duration: 7000, isClosable: true, position: 'top'
        });
    }, [roadBeingMapped, toast, logger]);

    // --- UPDATE: v5.1 — recommended-action buttons in the View modal's AI prediction card
    const handleViewAiAction = useCallback((intent, road) => {
        if (!road) return;
        viewRoadModal.onClose();
        switch (intent) {
            case 'CREATE_WO': handleCreateWO(road); break;
            case 'SCHEDULE_INSPECTION': handleScheduleInsp(road); break;
            case 'ASSESS': handleAssess(road); break;
            case 'EDIT': handleEdit(road); break;
            case 'MAP': handleStartMappingRoad(road); break;
            case 'RUN_AI': handleForecastRisk(); break;
            case 'REPORT': handleOpenReport(road); break; // --- UPDATE
            default: break;
        }
    }, [viewRoadModal, handleCreateWO, handleScheduleInsp, handleAssess, handleEdit, handleStartMappingRoad, handleForecastRisk, handleOpenReport]);
    // --- END UPDATE

    const handleCancelMappingRoad = useCallback(() => {
        if (!roadBeingMapped) return;
        logger.info('Drawing cancelled by user', { id: roadBeingMapped.id });
        setRoadBeingMapped(null);
        toast({ title: 'Drawing Cancelled', description: 'No changes were saved.', status: 'warning', duration: 2500 });
    }, [roadBeingMapped, toast, logger]);

    const handleSaveRoadMapping = useCallback(async (points) => {
        if (!roadBeingMapped) { logger.warn('Save called with no active road'); return; }
        if (!Array.isArray(points) || points.length < 2) {
            toast({ title: 'Need at least 2 points', description: 'A road must have a start and end point.', status: 'warning', duration: 3000 });
            return;
        }

        setIsSavingGeometry(true);
        try {
            const geoJson = { type: 'LineString', coordinates: points.map(p => [p.lng, p.lat]) };
            const lengthM = polylineLengthM(points);
            const centerLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
            const centerLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;

            logger.info('Saving road geometry and polyline array', {
                id: roadBeingMapped.id, pointsCount: points.length, center: { lat: centerLat, lng: centerLng }
            });

            await roadService.updateRoadCoordinates(roadBeingMapped.id, {
                geo_point: geoJson,
                latitude: centerLat,
                longitude: centerLng,
                polyline_points: points,
                metadata: {
                    ...(roadBeingMapped.metadata || {}),
                    length_m: Math.round(lengthM),
                    geometry_source: 'manual_draw',
                    geometry_updated_at: new Date().toISOString(),
                },
            });

            logger.success('Road geometry and polyline points saved', { id: roadBeingMapped.id });
            toast({
                title: '✅ Road Mapped Successfully',
                description: `${roadBeingMapped.name} now has ${points.length} waypoints saved to the network inventory.`,
                status: 'success', duration: 4000, isClosable: true,
            });
            setRoadBeingMapped(null);
            fetchData(true);
        } catch (error) {
            logger.error('Road geometry save failed', error);
            toast({ title: 'Save Failed', description: error.message || 'Could not persist road geometry.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            if (isMounted.current) setIsSavingGeometry(false);
        }
    }, [roadBeingMapped, toast, fetchData, logger]);

    // ── Modal submits ───────────────────────────────────────────────────────
    const submitCreateRoad = useCallback(async (payload) => {
        try {
            await roadService.createRoad(payload);
            logger.success('New road created', payload?.metadata?.road_code);
            toast({ title: 'Road Added Successfully', status: 'success', duration: 3000 });
            fetchData();
        } catch (err) {
            logger.error('Failed to create road', err);
            toast({ title: 'Creation Failed', description: err.message, status: 'error', duration: 4000 });
            throw err;
        }
    }, [toast, fetchData, logger]);

    const submitAssessment = useCallback(async (payload) => {
        try {
            const res = await roadService.recordConditionAssessment(activeRoad.id, payload);
            logger.success('Assessment recorded', res?.data);
            toast({
                title: res?.data?.auto_flagged ? '⚠ Assessment recorded — road auto-flagged' : 'Assessment recorded',
                description: `Score: ${res?.data?.condition_score} (${res?.data?.condition_rating})`,
                status: res?.data?.auto_flagged ? 'warning' : 'success',
                duration: 3000,
            });
            fetchData();
        } catch (err) {
            logger.error('Assessment failed', err);
            toast({ title:'Assessment failed', description:err.message, status:'error', duration:3000 });
            throw err;
        }
    }, [activeRoad, toast, fetchData, logger]);

    const submitInspection = useCallback(async (payload) => {
        try {
            await roadService.scheduleInspection(activeRoad.id, payload);
            logger.success('Inspection scheduled');
            toast({ title:'Inspection scheduled', status:'success', duration:2000 });
            fetchData();
        } catch (err) {
            toast({ title:'Schedule failed', description:err.message, status:'error', duration:3000 });
            throw err;
        }
    }, [activeRoad, toast, fetchData, logger]);

    const submitIssue = useCallback(async (payload) => {
        try {
            await roadService.reportRoadIssue(activeRoad.id, payload);
            logger.success('Issue reported');
            toast({
                title: payload.severity === 'high' || payload.severity === 'critical'
                    ? '🤖 Issue reported — work order auto-created' : 'Issue reported',
                status:'success', duration: 3000,
            });
            fetchData();
        } catch (err) {
            toast({ title:'Report failed', description:err.message, status:'error', duration:3000 });
            throw err;
        }
    }, [activeRoad, toast, fetchData, logger]);

    const submitWO = useCallback(async (payload) => {
        try {
            await roadService.createRoadWorkOrder(activeRoad.id, payload);
            logger.success('Work order created');
            toast({ title:'Work order created', status:'success', duration:2000 });
            fetchData();
        } catch (err) {
            toast({ title:'WO creation failed', description:err.message, status:'error', duration:3000 });
            throw err;
        }
    }, [activeRoad, toast, fetchData, logger]);

    // --- UPDATE: v5.5 keep the active road in step with the reloaded list
    useEffect(() => {
        if (!activeRoad || editRoadModal.isOpen) return; // never reset a form being edited (auto-refresh)
        const fresh = roads.find(r => String(r.id) === String(activeRoad.id));
        if (fresh && fresh !== activeRoad) setActiveRoad(fresh);
    }, [roads]); // eslint-disable-line react-hooks/exhaustive-deps
    // --- END UPDATE

    // ── Derived data ────────────────────────────────────────────────────────
    const totalKm = useMemo(() => {
        const totalLengthM = roads.reduce((s, r) => s + (Number(r.metadata?.length_m) || 0), 0);
        return (totalLengthM / 1000).toFixed(1);
    }, [roads]);

    // single pass split into mapped / unmapped
    const { mappedRoads, unmappedRoads } = useMemo(() => {
        const mapped = [], unmapped = [];
        roads.forEach(r => (isRoadMapped(r) ? mapped : unmapped).push(r));
        return { mappedRoads: mapped, unmappedRoads: unmapped };
    }, [roads]);

    // --- UPDATE: one pass computes avg RUL, critical-risk count and coverage for the AI strip
    const rulStats = useMemo(() => {
        let sum = 0, scored = 0, critical = 0;
        roads.forEach(r => {
            const y = r.ai_rul?.predicted_rul_years;
            if (y != null && Number.isFinite(Number(y))) {
                sum += Number(y); scored += 1;
                if (rulBand(y)?.key === 'critical') critical += 1;
            }
        });
        return { averageRUL: scored ? (sum / scored).toFixed(1) : 0, scored, critical };
    }, [roads]);
    const averageRUL = rulStats.averageRUL;
    // --- END UPDATE

    // --- UPDATE: v5.6 segments of the roads drawn on the map (only while the map shows segment health)
    const mappedRoadIds = useMemo(() => mappedRoads.map(r => r.id), [mappedRoads]);
    // --- UPDATE v5.7: whole network (all=1) or just the inventory page; Roads mode also needs the
    // network list when "Whole network" is chosen
    const segmentMap = useRoadSegmentMap(mappedRoadIds, {
        enabled: activeTab === TAB_MAP && (mapMode === 'segments' || mapScope === 'network'),
        all: mapScope === 'network',
    });
    // --- END UPDATE
    const segmentSummary = useMemo(() => (segmentMap.data ? summarize(filterSegments(segmentMap.data.segments, null)) : null), [segmentMap.data]);
    const toggleSegmentBand = useCallback((key) => {
        setSegmentBands(prev => {
            const n = new Set(prev);
            if (n.has(key)) n.delete(key); else n.add(key);
            return n.size ? n : new Set(BAND_KEYS); // never hide everything by accident
        });
    }, []);
    const showAllSegmentBands = useCallback(() => setSegmentBands(new Set(BAND_KEYS)), []);
    const handleSegmentSelect = useCallback((seg) => setSelectedSegmentId(seg?.id ?? null), []);
    const { sliceRoads } = segmentMap;
    const handleSliceUnsegmented = useCallback(async () => {
        const ids = segmentMap.data?.unsegmented || [];
        if (!ids.length) return;
        // --- UPDATE v5.7: the whole network can hold many unsliced roads
        if (ids.length > 25 && !window.confirm(`Slice ${ids.length} roads into ~100 m segments? This runs on the server and can take a few minutes.`)) return;
        // --- END UPDATE
        const r = await sliceRoads(ids);
        toast({
            title: r.sliced ? `${r.sliced} road(s) sliced into segments` : 'No roads were sliced',
            description: r.failed ? `${r.failed} road(s) could not be sliced — check that their drawn path has at least two points.` : 'Run "Evaluate with ML" in each Works Report to score the new segments.',
            status: r.failed && !r.sliced ? 'warning' : 'success', duration: 5000, isClosable: true,
        });
    }, [segmentMap.data, sliceRoads, toast]);
    // --- END UPDATE


    // Heatmap colours share the RUL band thresholds (≤2 red, ≤5 orange, ≤8 yellow, else green)
    const heatmapRoads = useMemo(() => {
        if (!heatmapActive) return mappedRoads;
        return mappedRoads.map(r => {
            if (!r.ai_rul?.predicted_rul_years) return r;
            const color = (rulBand(r.ai_rul.predicted_rul_years) || RUL_BANDS[3]).hex;
            return { ...r, metadata: { ...r.metadata, strokeColor: color, fillColor: color } };
        });
    }, [mappedRoads, heatmapActive]);

    // --- UPDATE v5.7: every mapped road for the map (inventory page roads keep their full data / AI colours)
    const mapAssets = useMemo(() => {
        if (mapScope !== 'network' || !segmentMap.data?.roads?.length) return heatmapRoads;
        const onPage = new Set(mappedRoads.map(r => String(r.id)));
        const extra = segmentMap.data.roads
            .filter(r => !onPage.has(String(r.id)) && Array.isArray(r.polyline) && r.polyline.length >= 2)
            .map(r => ({
                id: r.id,
                name: r.name,
                type: 'road',
                status: r.status,
                polyline_points: r.polyline,
                open_work_orders_count: r.open_work_orders,
                metadata: {
                    road_code: r.road_code, condition_rating: r.condition_rating, condition_score: r.condition_score,
                    classification: r.classification, constituency: r.constituency, length_m: r.length_m, ml_pci: r.ml_pci,
                },
                _networkOnly: true,
            }));
        return extra.length ? [...heatmapRoads, ...extra] : heatmapRoads;
    }, [mapScope, segmentMap.data, heatmapRoads, mappedRoads]);
    const networkSummary = useMemo(() => {
        if (!segmentMap.data) return null;
        const roadsCount = segmentMap.data.roads.length;
        return { roads: roadsCount, segmented_roads: roadsCount - (segmentMap.data.unsegmented?.length || 0) };
    }, [segmentMap.data]);
    const handleFitNetwork = useCallback(() => {
        const map = mapInstanceRef.current;
        if (!map) return;
        const pts = [];
        (segmentMap.data?.roads || []).forEach(r => (r.polyline || []).forEach(p => pts.push([p.lat, p.lng])));
        if (!pts.length) mappedRoads.forEach(r => { if (r.latitude && r.longitude) pts.push([Number(r.latitude), Number(r.longitude)]); });
        if (!pts.length) return;
        try {
            let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity;
            pts.forEach(([la, ln]) => { if (la < minLat) minLat = la; if (la > maxLat) maxLat = la; if (ln < minLng) minLng = ln; if (ln > maxLng) maxLng = ln; });
            map.fitBounds([[minLat, minLng], [maxLat, maxLng]], { padding: [40, 40], maxZoom: 17 });
        } catch { /* map not ready */ }
    }, [segmentMap.data, mappedRoads]);
    const handleMapReady = useCallback((map) => { mapInstanceRef.current = map || null; }, []); // --- UPDATE v5.9: null when the map unmounts
    // --- END UPDATE

    // --- UPDATE v5.8: road ponding forecast (hook is called above useRoadWorksData)
    const handlePondingFocus = useCallback((seg) => {
        if (!seg) return;
        setPondingSegmentId(seg.segment_id);
        setShowPondingLayer(true);
        const p = seg.point || seg.start;
        const map = mapInstanceRef.current;
        if (p && map) { try { map.flyTo([p.lat, p.lng], Math.max(map.getZoom?.() ?? 16, 18), { duration: 0.8 }); } catch { /* map not ready */ } }
    }, []);
    const handlePondingOpenReport = useCallback((seg) => {
        if (seg?.road_id) handleOpenReport({ id: seg.road_id, name: seg.road_name });
    }, [handleOpenReport]);
    // --- UPDATE v5.9
    const handlePondingSelect = useCallback((seg) => setPondingSegmentId(seg?.segment_id ?? null), []);
    const pondingCardRef = useRef(null);
    const openPondingCard = useCallback(() => {
        setShowPondingLayer(true);
        try { pondingCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* jsdom */ }
    }, []);
    const pondingSummary = useMemo(() => {
        const d = ponding.data;
        if (!d) return null;
        const s = d.summary || {};
        return {
            count: s.ponding_segments || 0, roads: s.roads_affected || 0,
            maxDepth: s.max_depth_m ? fmtPondDepth(s.max_depth_m) : '—',
            starts: s.earliest_ponding_hours != null ? fmtPondHours(s.earliest_ponding_hours) : null,
            scheme: PONDING_SIGNAL[d.signal]?.scheme || 'blue', signal: d.signal,
        };
    }, [ponding.data]);
    const showPondingFromAi = useCallback(() => { setActiveTab(TAB_MAP); openPondingCard(); }, [openPondingCard]);
    // --- END UPDATE
    // --- END UPDATE

    const handleRunClustering = useCallback(async () => {
        setIsClustering(true);
        try {
            const res = await roadAIEngineService.triggerAnomalyClustering();
            toast({
                title: 'AI Clustering Complete',
                description: `Grouped raw anomalies into ${res?.clusters || 0} actionable Work Orders.`,
                status: 'success', duration: 5000, isClosable: true
            });
            fetchData(true);
        } catch (error) {
            toast({ title: 'Clustering Failed', description: error.message, status: 'error' });
        } finally {
            if (isMounted.current) setIsClustering(false);
        }
    }, [toast, fetchData]);

    const toggleHeatmap = useCallback(() => setHeatmapActive(v => !v), []);
    // --- UPDATE (Road insights v2): late-declared handlers for insight actions
    insightExtrasRef.current = { handleForecastRisk, handleRunClustering, handleOpenReport, setHeatmapActive };
    // --- END UPDATE

    const handleAssetSelect = useCallback((road) => {
        if (roadBeingMapped) return;
        setActiveRoad(road);
        // --- UPDATE: the map selection is also the road shown in the Works Report tab
        if (road?.id) setReportRoad({ id: road.id, name: road.name || null });
        toast({
            title: `Selected ${road.name}`,
            description: 'Open the Road Works Report tab for segments, inspections and work orders.',
            status: 'info', duration: 2500,
        });
        // --- END UPDATE
    }, [roadBeingMapped, toast]);

    const handleViewAllStarved = useCallback(() => {
        // setter resets to page 1 and the hook refetches once (v4.7 fetched twice here)
        setShowOnlyStarved(true);
        toast({ title: 'Filtered to Starved Assets', status: 'info' });
    }, [setShowOnlyStarved, toast]);

    const handleSyncWithML = useCallback(() => fetchData(true), [fetchData]);

    // --- UPDATE: AI strip interactions
    const toggleCriticalRul = useCallback(() => {
        setRulFilter(f => (f === 'critical' ? null : 'critical'));
        setActiveTab(0);
    }, []);
    const openMap = useCallback(() => setActiveTab(1), []);
    const handleSelectCondition = useCallback(
        (c) => setConditionFilter(conditionFilter === c ? '' : c),
        [conditionFilter, setConditionFilter]
    );
    const onAiSyncComplete = useCallback(() => { setSelected(new Set()); fetchData(true); }, [fetchData]);

    // --- UPDATE: v5.2 Road Works Report tab data
    // First visit without a chosen road → the selected/active road, else the first road on the page
    useEffect(() => {
        if (activeTab !== TAB_REPORT || reportRoad || !roads.length) return;
        const first = (activeRoad && roads.find(r => String(r.id) === String(activeRoad.id))) || roads[0];
        setReportRoad({ id: first.id, name: first.name || null });
    }, [activeTab, reportRoad, roads, activeRoad]);

    // Deep-linked road: fill in its name once it is on the loaded page
    useEffect(() => {
        if (!reportRoad || reportRoad.name) return;
        const match = roads.find(r => String(r.id) === String(reportRoad.id));
        if (match?.name) setReportRoad({ id: match.id, name: match.name });
    }, [roads, reportRoad]);

    // --- UPDATE: v5.3 table selection → report below (scrolls into view)
    const handleSelectReportRoad = useCallback((road) => {
        if (!road?.id) return;
        setReportRoad(prev => (prev && String(prev.id) === String(road.id) ? prev : { id: road.id, name: road.name || null }));
        requestAnimationFrame(() => {
            try { reportPanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }); } catch { /* jsdom / old browsers */ }
        });
    }, []);
    const reportTotalPages = useMemo(() => Math.max(1, Math.ceil((Number(totalRoads) || 0) / (Number(limit) || 1))), [totalRoads, limit]);
    // --- END UPDATE
    const handleReportDataChanged = useCallback(() => {
        fetchData(true);
        setReportRefreshKey(k => k + 1); // --- UPDATE v5.3: table roll-up too
    }, [fetchData]);
    // --- END UPDATE
    const openHelp = useCallback(() => setShowQuickRef(true), []);
    // --- END UPDATE

    const statCards = useMemo(() => [
        {
            key:'total', label:'Total Roads',
            value: analytics?.status_distribution?.reduce((a, c) => a + c.count, 0) || totalRoads || 0,
            icon: FaRoad, color:'blue', help:'Across all constituencies', live:false,
        },
        {
            key:'avgScore', label:'Avg Condition',
            value: analytics?.average_condition_score != null ? `${analytics.average_condition_score}` : '—',
            icon: MdAssessment, color:'green', help:'PCI 0–100 scale', live:false,
        },
        {
            key:'openWO', label:'Open Work Orders',
            value: analytics?.work_orders?.total_open || 0,
            icon: MdBuild, color:'orange',
            help: `${analytics?.work_orders?.critical || 0} critical`, live:true,
        },
        {
            key:'inspections', label:'Inspections',
            value: analytics?.inspections?.upcoming_30d || 0,
            icon: MdSchedule, color:'cyan', help:'Next 30 days', live:false,
        },
    ], [analytics, totalRoads]);

    // Evaluated only while the debug drawer is open
    const getRuntimeState = useCallback(() => {
        const diag = roadAIEngineService.getDiagnostics?.() || {};
        return {
            roads: roads.length,
            mapped: mappedRoads.length,
            totalUnmappedRoads,
            totalRoads,
            page,
            limit,
            selected: selected.size,
            autoRefresh: String(autoRefresh),
            wsConnected: String(isConnected),
            roadBeingMapped: roadBeingMapped?.name || '—',
            savingGeometry: String(isSavingGeometry),
            liveAiEvents: liveAiEvents.length,
            rulFilter: rulFilter || '—',
            aiEngineCircuit: diag?.circuit?.state || aiCircuit || '—',
            aiEngineCacheHits: diag?.cache?.hitRate || '—',
            aiEngineInflight: diag?.inflight ?? '—',
            lastRefresh: lastRefresh.toLocaleTimeString(),
            avgScore: analytics?.average_condition_score ?? '—',
            openWO: analytics?.work_orders?.total_open ?? '—',
        };
    }, [roads, mappedRoads, totalUnmappedRoads, totalRoads, page, limit, selected, autoRefresh,
        isConnected, roadBeingMapped, isSavingGeometry, liveAiEvents, rulFilter, aiCircuit, lastRefresh, analytics]);

    const tabSelected = { bg: 'blue.500', color: 'white', boxShadow: 'md' };

    return (
        <Box pt={{ base:'10px', md:'10px' }} bg={bgColor} minH="100vh" pb={14}>
            <Box px={{ base: 4, md: 6 }}>
                <RoadPageHeader
                    isMobile={isMobile}
                    aiStatus={aiStatus}
                    isConnected={isConnected}
                    autoRefresh={autoRefresh}
                    setAutoRefresh={setAutoRefresh}
                    lastRefresh={lastRefresh}
                    isLoading={isLoading}
                    onRefresh={fetchData}
                    onExport={handleExport}
                    isExporting={isExporting}
                    onForecast={handleForecastRisk}
                    isForecasting={isForecasting}
                    onAddRoad={createRoadModal.onOpen}
                    onOpenDebug={debugDrawer.onOpen}
                    onOpenHelp={openHelp}
                />

                {/* --- UPDATE: AI / ML command strip */}
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.08, ...ANIM.smoothEase }}>
                    <AiCommandStrip
                        averageRUL={averageRUL}
                        criticalRiskCount={rulStats.critical}
                        scoredCount={rulStats.scored}
                        pageCount={roads.length}
                        unclusteredCount={unclusteredCount}
                        liveEventsCount={liveAiEvents.length}
                        totalUnmappedRoads={totalUnmappedRoads}
                        aiCircuit={aiCircuit}
                        aiStatus={aiStatus}
                        isLoading={isLoading}
                        isForecasting={isForecasting}
                        criticalFilterActive={rulFilter === 'critical'}
                        onForecast={handleForecastRisk}
                        onToggleCriticalFilter={toggleCriticalRul}
                        onOpenMap={openMap}
                        onShowStarved={handleViewAllStarved}
                    />
                </MotionBox>
                {/* --- END UPDATE */}

                <Tabs variant="soft-rounded" colorScheme="blue" isLazy index={activeTab} onChange={setActiveTab}>
                    <Flex justify="space-between" align="center" mb={4}>
                        <TabList bg={cardBg} p={1.5} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor}
                            overflowX="auto" maxW="100%" /* --- UPDATE: 3 tabs on small screens */>
                            <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={tabSelected}>
                                <Icon as={MdDashboard} mr={2} /> Dashboard Overview
                            </Tab>
                            <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={tabSelected}>
                                <Icon as={MdOutlineMap} mr={2} /> GIS Network Map
                                {totalUnmappedRoads > 0 && (
                                    <Badge ml={2} colorScheme="orange" borderRadius="full" fontSize="2xs" px={2}>
                                        {totalUnmappedRoads}
                                    </Badge>
                                )}
                                {/* --- UPDATE */}
                                {unclusteredCount > 0 && (
                                    <Box as="span" ml={1.5}>
                                        <AiChip kind="anomaly" size="xs" label={String(unclusteredCount)}
                                            tooltip={`${unclusteredCount} unclustered dashcam anomalies`} />
                                    </Box>
                                )}
                                {/* --- END UPDATE */}
                            </Tab>
                            {/* --- UPDATE: v5.2 */}
                            <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={tabSelected} whiteSpace="nowrap">
                                <Icon as={MdConstruction} mr={2} /> Road Works Report
                                {reportRoad?.name && (
                                    <Badge ml={2} colorScheme="purple" borderRadius="full" fontSize="2xs" px={2} maxW="140px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                        {reportRoad.name}
                                    </Badge>
                                )}
                            </Tab>
                            {/* --- END UPDATE */}
                            {/* --- UPDATE (Phone AI Helpdesk): keep last (TAB_PUBLIC) */}
                            <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={tabSelected} whiteSpace="nowrap">
                                <Icon as={MdPhoneInTalk} mr={2} /> Public Reports
                                {publicOpenCount > 0 && (
                                    <Badge ml={2} colorScheme="red" borderRadius="full" fontSize="2xs" px={2}>{publicOpenCount}</Badge>
                                )}
                            </Tab>
                            {/* --- END UPDATE */}
                        </TabList>
                    </Flex>

                    <TabPanels>
                        <TabPanel p={0}>
                            {/* STAT CARDS */}
                            <SimpleGrid columns={{ base:2, md:4 }} spacing={4} mb={6}>
                                {statCards.map((card, i) => (
                                    <MotionBox key={card.key} {...ANIM.fadeInUp}
                                        transition={{ ...ANIM.stagger(i, 0.08), ...ANIM.smoothEase }}>
                                        <StatCard
                                            label={card.label} value={card.value} icon={card.icon}
                                            color={card.color} helpText={card.help}
                                            isLoading={isLoading && !analytics}
                                            liveIndicator={card.live}
                                        />
                                    </MotionBox>
                                ))}
                            </SimpleGrid>

                            {/* COMMAND CENTER ROW 1 */}
                            <Grid templateColumns={{ base:'1fr', lg:'1fr 1fr', xl:'1fr 1fr 1fr' }} gap={5} mb={5}>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.18, ...ANIM.smoothEase }}>
                                    <ConditionDistributionPanel
                                        data={analytics?.condition_distribution}
                                        isLoading={isLoading && !analytics}
                                        activeCondition={conditionFilter}
                                        onSelectCondition={handleSelectCondition} />
                                </MotionBox>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.24, ...ANIM.smoothEase }}>
                                    <AIInsightsPanel
                                        batchInsights={batchInsights}
                                        liveEvents={liveAiEvents}
                                        isLoading={isLoading && !batchInsights.length}
                                        onInsightAction={handleInsightAction}
                                    />
                                </MotionBox>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.30, ...ANIM.smoothEase }}>
                                    <SystemHealthPanel logger={logger} />
                                </MotionBox>
                            </Grid>

                            {/* COMMAND CENTER ROW 2 */}
                            <Grid templateColumns={{ base:'1fr', lg:'1fr 1fr' }} gap={5} mb={5}>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.32, ...ANIM.smoothEase }}>
                                    <SurfaceTypePanel data={analytics?.surface_distribution} isLoading={isLoading && !analytics} />
                                </MotionBox>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.38, ...ANIM.smoothEase }}>
                                    <UpcomingInspectionsPanel logger={logger} />
                                </MotionBox>
                            </Grid>

                            {/* WATCHDOG PANEL */}
                            <Box mb={5}>
                                <RoadSystemWatchdogPanel
                                    roads={roads}
                                    onSyncWithML={handleSyncWithML}
                                    onViewAllStarved={handleViewAllStarved}
                                />
                            </Box>

                            {/* --- UPDATE: v5.4 DRIVE MODE AI DETECTIONS + DETECTION MODEL */}
                            <Grid templateColumns={{ base: '1fr', xl: '3fr 2fr' }} gap={5} mb={5}>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay: 0.40, ...ANIM.smoothEase }} minW={0}>
                                    <RoadDriveDetectionsPanel onSelectRoad={handleOpenReport} onDataChanged={handleReportDataChanged} />
                                </MotionBox>
                                <MotionBox {...ANIM.fadeInUp} transition={{ delay: 0.42, ...ANIM.smoothEase }} minW={0}>
                                    <RoadVisionModelPanel />
                                </MotionBox>
                            </Grid>
                            {/* --- END UPDATE */}

                            {/* ROAD INVENTORY TABLE */}
                            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.44, ...ANIM.smoothEase }} mb={6}>
                                <RoadInventoryTable
                                    roads={roads}
                                    isLoading={isLoading}
                                    isSearchPending={data.isSearchPending}
                                    fetchError={fetchError}
                                    onRetry={fetchData}
                                    lookups={lookups}
                                    totalRoads={totalRoads}
                                    totalKm={totalKm}
                                    page={page}
                                    setPage={setPage}
                                    limit={limit}
                                    setLimit={data.setLimit}
                                    sortBy={data.sortBy}
                                    sortDir={data.sortDir}
                                    onSort={data.handleSort}
                                    search={data.search}
                                    setSearch={data.setSearch}
                                    flushSearch={data.flushSearch}
                                    statusFilter={data.statusFilter}
                                    setStatusFilter={setStatusFilter}
                                    classificationFilter={data.classificationFilter}
                                    setClassificationFilter={data.setClassificationFilter}
                                    conditionFilter={conditionFilter}
                                    setConditionFilter={setConditionFilter}
                                    showOnlyStarved={data.showOnlyStarved}
                                    setShowOnlyStarved={setShowOnlyStarved}
                                    hasActiveFilters={data.hasActiveFilters}
                                    clearFilters={data.clearFilters}
                                    rulFilter={rulFilter}
                                    setRulFilter={setRulFilter}
                                    selected={selected}
                                    onToggle={toggleSelect}
                                    onSelectIds={selectIds}
                                    onClearSelection={clearSelection}
                                    bulkStatus={bulkStatus}
                                    setBulkStatus={setBulkStatus}
                                    onBulkStatusUpdate={handleBulkStatusUpdate}
                                    isBulkRunning={isBulkRunning}
                                    onOpenAiSync={aiSyncModal.onOpen}
                                    onView={handleView}
                                    onEdit={handleEdit}
                                    onCreateWO={handleCreateWO}
                                    onAssess={handleAssess}
                                    onScheduleInsp={handleScheduleInsp}
                                    onReportIssue={handleReportIssue}
                                    onDelete={handleDelete}
                                    // --- UPDATE: AI wiring
                                    onRunAI={handleForecastRisk}
                                    isRunningAI={isForecasting}
                                    onMapRoad={handleStartMappingRoad}
                                    // --- END UPDATE
                                    onOpenReport={handleOpenReport} // --- UPDATE: v5.2
                                />
                            </MotionBox>
                        </TabPanel>

                        <TabPanel p={0}>
                            <VStack spacing={5} align="stretch">
                                {/* --- UPDATE: v5.8 road ponding forecast */}
                                <Box ref={pondingCardRef} scrollMarginTop="80px">
                                <RoadPondingPanel
                                    forecast={ponding.data}
                                    loading={ponding.loading}
                                    error={ponding.error}
                                    stale={ponding.stale}
                                    onRefresh={ponding.refresh}
                                    hour={ponding.hour}
                                    onHour={ponding.setHour}
                                    playing={ponding.playing}
                                    onPlay={ponding.setPlaying}
                                    showOnMap={showPondingLayer}
                                    onShowOnMap={setShowPondingLayer}
                                    onFocusSegment={handlePondingFocus}
                                    onOpenReport={handlePondingOpenReport}
                                />
                                </Box>
                                {/* --- END UPDATE */}
                                {/* --- UPDATE: v5.6 map layers (roads / segment health, markers, labels, health bands) */}
                                <RoadMapLayerControls
                                    mode={mapMode}
                                    onModeChange={setMapMode}
                                    showMarkers={showMapMarkers}
                                    onShowMarkersChange={setShowMapMarkers}
                                    showAnomalies={showMapAnomalies}
                                    onShowAnomaliesChange={setShowMapAnomalies}
                                    showLabels={showSegmentLabels}
                                    onShowLabelsChange={setShowSegmentLabels}
                                    visibleBands={segmentBands}
                                    onToggleBand={toggleSegmentBand}
                                    onShowAllBands={showAllSegmentBands}
                                    summary={segmentSummary}
                                    loading={segmentMap.loading}
                                    error={segmentMap.error}
                                    warnings={segmentMap.warnings}
                                    unsegmentedCount={segmentMap.data?.unsegmented?.length || 0}
                                    onSliceUnsegmented={handleSliceUnsegmented}
                                    slicing={segmentMap.slicing}
                                    onRefresh={segmentMap.refresh}
                                    isDrawing={!!roadBeingMapped}
                                    // --- UPDATE v5.7
                                    scope={mapScope}
                                    onScopeChange={setMapScope}
                                    networkSummary={networkSummary}
                                    onFitNetwork={handleFitNetwork}
                                    // --- END UPDATE
                                    // --- UPDATE v5.9: rain-water layer switch + forecast chip
                                    showPonding={showPondingLayer}
                                    onShowPondingChange={setShowPondingLayer}
                                    pondingSummary={pondingSummary}
                                    onOpenPonding={openPondingCard}
                                    // --- END UPDATE
                                />
                                {/* --- END UPDATE */}
                                <MotionBox {...ANIM.fadeInUp} transition={{ duration: 0.4 }} h="65vh" minH="500px">
                                    <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
                                        borderColor={roadBeingMapped ? 'blue.400' : borderColor}
                                        boxShadow={roadBeingMapped
                                            ? '0 0 0 3px var(--chakra-colors-blue-100), 0 8px 24px rgba(49,130,206,0.2)'
                                            : 'sm'}
                                        overflow="hidden" h="100%" position="relative" transition="all 0.3s"
                                    >
                                        <MotionBox
                                            position="absolute" top={0} left={0} right={0} h="3px"
                                            bgGradient={roadBeingMapped
                                                ? 'linear(to-r, blue.400, cyan.400, blue.400)'
                                                : 'linear(to-r, cyan.400, blue.500)'}
                                            zIndex={10}
                                            animate={roadBeingMapped ? { opacity: [1, 0.5, 1] } : {}}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        />

                                        <AIOperationsPanel
                                            unclusteredCount={unclusteredCount}
                                            isClustering={isClustering}
                                            onRunClustering={handleRunClustering}
                                            heatmapActive={heatmapActive}
                                            onToggleHeatmap={toggleHeatmap}
                                            averageRUL={averageRUL}
                                            // --- UPDATE: AI wiring
                                            roads={roads}
                                            onRunPrediction={handleForecastRisk}
                                            isPredicting={isForecasting}
                                            aiCircuit={aiCircuit}
                                            lastRefresh={lastRefresh}
                                            onSelectRoad={handleView}
                                            unmappedCount={totalUnmappedRoads}
                                            // --- END UPDATE
                                            // --- UPDATE v5.9
                                            pondingSummary={pondingSummary}
                                            onShowPonding={openPondingCard}
                                            // --- END UPDATE
                                        />

                                        <AnimatePresence>
                                            {roadBeingMapped && (
                                                <MotionBox
                                                    initial={{ opacity: 0, scale: 0.8, x: 20 }}
                                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                                    exit={{ opacity: 0, scale: 0.8, x: 20 }}
                                                    position="absolute" top={4} right={4} zIndex={1000}
                                                    bg={overlayBg} borderRadius="xl" boxShadow="0 8px 24px rgba(0,0,0,0.15)"
                                                    px={4} py={3} borderWidth="2px" borderColor="blue.400"
                                                    pointerEvents="auto"
                                                >
                                                    <HStack spacing={3}>
                                                        <MotionBox animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                                                            <Icon as={MdEditLocation} color="blue.500" boxSize={6} />
                                                        </MotionBox>
                                                        <Box>
                                                            <Text fontSize="xs" fontWeight="700" color="blue.500" textTransform="uppercase" letterSpacing="wider">
                                                                Drawing
                                                            </Text>
                                                            <Text fontSize="sm" fontWeight="800" color={overlayText} noOfLines={1} maxW="200px">
                                                                {roadBeingMapped.name}
                                                            </Text>
                                                            {isSavingGeometry && (
                                                                <HStack spacing={1} mt={0.5}>
                                                                    <Spinner size="xs" color="blue.500" />
                                                                    <Text fontSize="2xs" color="blue.500">Saving…</Text>
                                                                </HStack>
                                                            )}
                                                        </Box>
                                                    </HStack>
                                                </MotionBox>
                                            )}
                                        </AnimatePresence>

                                        <GisMapView
                                            assets={mapAssets} /* --- UPDATE v5.7: whole network (was heatmapRoads) */
                                            onMapReady={handleMapReady} /* --- UPDATE v5.7 */
                                            anomalies={rawAnomalies}
                                            pipelines={EMPTY_ARR}
                                            sensors={EMPTY_ARR}
                                            readings={EMPTY_OBJ}
                                            isLoadingData={isLoading}
                                            isDrawingRoad={!!roadBeingMapped}
                                            onRoadMappingComplete={handleSaveRoadMapping}
                                            onAssetSelect={handleAssetSelect}
                                            // --- UPDATE: v5.6 segment health layer + marker toggles
                                            roadSegments={segmentMap.data}
                                            roadDisplayMode={mapMode}
                                            segmentBands={segmentBands}
                                            showSegmentLabels={showSegmentLabels}
                                            selectedSegmentId={selectedSegmentId}
                                            onSegmentSelect={handleSegmentSelect}
                                            onOpenRoadReport={handleOpenReport}
                                            showAssetMarkers={showMapMarkers}
                                            showSensorMarkers={showMapMarkers}
                                            showAnomalies={showMapAnomalies}
                                            // --- END UPDATE
                                            // --- UPDATE v5.9: road ponding forecast layer + legend
                                            pondingForecast={ponding.data}
                                            showPonding={showPondingLayer && activeTab === TAB_MAP}
                                            pondingHour={ponding.hour}
                                            selectedPondingSegmentId={pondingSegmentId}
                                            onPondingSegmentSelect={handlePondingSelect}
                                            onPondingOpenReport={handlePondingOpenReport}
                                            // --- END UPDATE
                                        />
                                        {/* --- UPDATE v5.9: the ponding layer is drawn inside GisMapView (pondingForecast props) */}
                                    </Box>
                                </MotionBox>

                                <RoadMapDrawingPanel
                                    unmappedRoads={unmappedRoads}
                                    isLoading={isLoading}
                                    roadBeingMapped={roadBeingMapped}
                                    onStartMapping={handleStartMappingRoad}
                                    onCancelMapping={handleCancelMappingRoad}
                                />

                                <AIClusteredWorkOrdersTable />
                            </VStack>
                        </TabPanel>

                        {/* --- UPDATE: v5.2 ROAD WORKS REPORT */}
                        <TabPanel p={0}>
                            <VStack spacing={4} align="stretch">
                                {/* --- UPDATE: v5.3 road report table (AI · ML · segments · WOs · QA · inspections) */}
                                <RoadReportSelectorTable
                                    roads={roads}
                                    selectedRoadId={reportRoad?.id}
                                    onSelectRoad={handleSelectReportRoad}
                                    refreshKey={reportRefreshKey}
                                    onMapRoad={handleStartMappingRoad}
                                    onRunAI={handleForecastRisk}
                                    isRunningAI={isForecasting}
                                    onViewRoad={handleView}
                                    page={page}
                                    totalPages={reportTotalPages}
                                    totalRoads={totalRoads}
                                    onPageChange={setPage}
                                    isLoadingRoads={isLoading}
                                />
                                <Box ref={reportPanelRef} scrollMarginTop="80px">
                                {/* --- END UPDATE */}
                                <RoadWorksReportPanel
                                    activeRoadId={reportRoad?.id ?? null}
                                    roadName={reportRoad?.name || undefined}
                                    embedded
                                    onDataChanged={handleReportDataChanged}
                                />
                                </Box>{/* --- UPDATE v5.3 */}
                            </VStack>
                        </TabPanel>
                        {/* --- END UPDATE */}
                        {/* --- UPDATE (Phone AI Helpdesk): PUBLIC REPORTS — phone line + helpdesk road reports */}
                        <TabPanel p={0}>
                            <RoadPublicReportsPanel
                                roads={roads}
                                focusIssueId={deepLink.issue}
                                onCount={setPublicOpenCount}
                                onOpenRoad={(road) => { setActiveTab(TAB_REPORT); handleSelectReportRoad(road); }}
                            />
                        </TabPanel>
                        {/* --- END UPDATE */}
                    </TabPanels>
                </Tabs>

                <AIRoadStateSyncModal
                    isOpen={aiSyncModal.isOpen}
                    onClose={aiSyncModal.onClose}
                    selectedRoadIds={selected}
                    roads={roads}
                    onComplete={onAiSyncComplete}
                    logger={logger}
                />

                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.55, ...ANIM.smoothEase }} mt={6}>
                    <QuickReferencePanel showQuickRef={showQuickRef} setShowQuickRef={setShowQuickRef} />
                </MotionBox>
            </Box>

            <CreateRoadModal isOpen={createRoadModal.isOpen} onClose={createRoadModal.onClose} onSubmit={submitCreateRoad} />
            <ViewRoadModal isOpen={viewRoadModal.isOpen} onClose={viewRoadModal.onClose} road={activeRoad} /* --- UPDATE */ onAiAction={handleViewAiAction} /* --- END UPDATE */ />
            <EditRoadModal isOpen={editRoadModal.isOpen} onClose={editRoadModal.onClose} road={activeRoad} onSubmit={submitEditRoad} />
            <QuickWorkOrderModal isOpen={woModal.isOpen} onClose={woModal.onClose} road={activeRoad} onSubmit={submitWO} initialValues={woInitialValues} />
            <ConditionAssessmentModal isOpen={conditionModal.isOpen} onClose={conditionModal.onClose} road={activeRoad} onSubmit={submitAssessment} />
            <ScheduleInspectionModal isOpen={inspectionModal.isOpen} onClose={inspectionModal.onClose} road={activeRoad} onSubmit={submitInspection} />
            <ReportIssueModal isOpen={issueModal.isOpen} onClose={issueModal.onClose} road={activeRoad} onSubmit={submitIssue} />

            {process.env.NODE_ENV === 'development' && (
                <RoadDebugDrawer
                    isOpen={debugDrawer.isOpen}
                    onClose={debugDrawer.onClose}
                    logger={logger}
                    getRuntimeState={getRuntimeState}
                />
            )}
        </Box>
    );
};

export default RoadWorksPage;
